import React, { useCallback, useEffect, useRef } from "react";
import { styled } from "../stitches.config";
import useWebSocket from "react-use-websocket";

import { AnyBlock, WebBlock as WebBlockType } from "../types";
import { gridToPixelSize } from "../utils/gridPosition";
import { handleScreenshot } from "../utils/fileUpload";
import { Button } from "../components/Button";
import Icon from "../components/Icon";
import { GRID_SEAM } from "../consts";
import { brown } from "../stitches.config";

const cloudTabWSURL = "wss://tabspace.xyz/";

export const WEB_UI_HEIGHT = 48;

// this is needed because it needs to be a non passive event listener in order to be able to cancel wheel events
function useHookWithRefCallback(sendTabEvent: Function) {
  const setRef = useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        let newListener = (e: Event) => {
          e.preventDefault();
          e.stopPropagation(); // stop normal block dragging

          let packet = {
            type: "wheel",
            data: { deltaY: (e as WheelEvent).deltaY },
          };
          sendTabEvent(packet);
          return false;
        };

        node.onwheel = newListener;
      }

      return () => {};
    },
    [sendTabEvent]
  );

  return [setRef];
}

function WebBlock({
  block,
  updateBlock,
  upsertBlock,
}: {
  block: WebBlockType;
  updateBlock: (block: Partial<AnyBlock>) => void;
  upsertBlock: (block: AnyBlock) => void;
}) {
  const {
    sendJsonMessage,
    lastMessage,
    readyState,
    // getWebSocket,
  } = useWebSocket(cloudTabWSURL, {
    onOpen: () => {},
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: (closeEvent) => true,
  });
  // this should be part of a custom hook
  let sendTabEvent = useCallback(
    (packet) => {
      packet.tabId = block.id;
      sendJsonMessage(packet);
    },
    [sendJsonMessage, block.id]
  );

  useEffect(() => {
    sendTabEvent({
      type: "hello",
      data: textInputRef!.current!.value,
    });
  }, [sendTabEvent, readyState]);

  useEffect(() => {
    const pixelSize = gridToPixelSize(block.size);
    pixelSize.width -= GRID_SEAM + 2; // Hack, these 2 pixels are needed to match the returned image to the intrinsic size of the div.
    pixelSize.height -= GRID_SEAM + 2;
    pixelSize.height -= WEB_UI_HEIGHT; //hack to subtract header height
    sendTabEvent({
      type: "resize",
      data: pixelSize,
    });
  }, [block.size, sendTabEvent, readyState]);

  const textInputRef = useRef<HTMLInputElement>(null);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    e.stopPropagation(); // prevent canvas key commands from conflicting
    switch (e.key) {
      case "Escape":
        textInputRef!.current!.blur();
        break;
      case "Enter":
        sendTabEvent({
          type: "url",
          data: textInputRef!.current!.value,
        });
        textInputRef!.current!.blur();
        break;
      default:
    }
  }
  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    updateBlock({
      value: e.target.value,
    });
  }

  const [imageRef] = useHookWithRefCallback(sendTabEvent);

  return (
    <BrowserContainer>
      <PortalControls>
        <BlockInput
          type="text"
          onChange={onChange}
          onKeyDown={onKeyDown}
          onMouseDown={(e) => e.stopPropagation()} // prevent moving/selecting block
          value={block.value}
          placeholder="Enter a URL"
          ref={textInputRef}
        ></BlockInput>
        <GoButton
          onClick={() => {
            sendTabEvent({
              type: "url",
              data: textInputRef!.current!.value,
            });
          }}
          title="Refresh page"
          aria-label="Refresh page"
          marginOff
        >
          <Icon icon="Refresh" color={brown} />
        </GoButton>

        <PrintButton
          onClick={() => {
            handleScreenshot(lastMessage?.data, upsertBlock, {
              x: block.position.x + block.size.width,
              y: block.position.y + 2, // Height of web UI
            });
          }}
          title="Snapshot page"
          aria-label="Snapshot page"
          marginOff
        >
          <PrintIcon>
            <Icon icon="Snapshot" color={brown} />
          </PrintIcon>
        </PrintButton>
      </PortalControls>

      <ImageContainer>
        <Img
          ref={imageRef}
          onMouseDown={(e: React.MouseEvent) => {
            (e.target as HTMLElement).focus();
            e.preventDefault();
            e.stopPropagation(); // stop normal block dragging
            let { top, left } = (e.target as Element).getBoundingClientRect();
            let packet = {
              type: "mousedown",
              data: { x: e.clientX - left, y: e.clientY - top },
            };
            sendTabEvent(packet);
          }}
          onMouseMove={(e) => {
            let { top, left } = (e.target as Element).getBoundingClientRect();
            let packet = {
              type: "mousemove",
              data: { x: e.clientX - left, y: e.clientY - top },
            };
            sendTabEvent(packet);
          }}
          onMouseUp={(e) => {
            let { top, left } = (e.target as Element).getBoundingClientRect();
            let packet = {
              type: "mouseup",
              data: { x: e.clientX - left, y: e.clientY - top },
            };
            sendTabEvent(packet);
          }}
          tabIndex={0}
          onKeyDown={(e) => {
            e.preventDefault();
            let packet = {
              type: "keydown",
              data: { key: e.key },
            };
            sendTabEvent(packet);
          }}
          onKeyUp={(e) => {
            let packet = {
              type: "keyup",
              data: { key: e.key },
            };
            sendTabEvent(packet);
          }}
          src={
            lastMessage?.data
              ? URL.createObjectURL(lastMessage?.data)
              : "https://media2.giphy.com/media/3oEjI6SIIHBdRxXI40/200.gif"
          }
          alt={`Live screenshot of website: ${block.value}`}
        />
      </ImageContainer>
    </BrowserContainer>
  );
}

export default WebBlock;

export const BrowserContainer = styled("div", {
  display: "grid",
  gridTemplateRows: `${WEB_UI_HEIGHT}px 1fr`,
  overflow: "hidden",
});

const PortalControls = styled("div", {
  display: "grid",
  gridTemplateColumns: "1fr auto auto",
  gridGap: "$2",
  overflow: "hidden",
  padding: "$2",
  alignItems: "center",
});
// Fixes bug with <img> object-fit not working well in grid-template
const ImageContainer = styled("div", {
  width: "100%",
  height: "100%",
  display: "grid",
  overflow: "hidden",
});

const Img = styled("img", {
  width: "100%",
  height: "100%",
  userSelect: "none",
  cursor: "none",
});

export const BlockInput = styled("input", {
  padding: "$2 $4",
  height: "$6",
  display: "grid",
  placeItems: "center",
  borderRadius: "$2",
  justifySelf: "start",
  alignSelf: "center",
  border: "1px solid $transparentBlack05",
  minWidth: "180px",
  width: "100%",
  backgroundColor: "$inputBackground",
  color: "$brown",
});

const GoButton = styled(Button, {
  padding: "0",
  height: "$6",
  width: "$6",
  display: "grid",
  placeItems: "center",
  justifySelf: "start",
  userSelect: "none",
});

const PrintButton = styled(GoButton, {
  padding: "0",
  height: "$6",
  width: "$6",
});

const PrintIcon = styled("div", {
  width: "$5",
  height: "$5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});
